import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { GridGrouping } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { kebabCase } from 'lodash-es';

import { RedirectHelperService } from '../../navigation-core/redirect-helper.service';
import { TournamentEventSection, TournamentLeague, TournamentParticipant, TournamentSection, TournamentViewModel } from './tournament.models';

export enum TournamentView {
    Club,
    Country,
    Default,
    MultiClub,
    MultiCountry,
}

export interface FixtureSection extends TournamentEventSection {
    grid: ObservableGrid;
}

export interface LeagueSection extends TournamentSection {
    leagues: TournamentLeague[];
}

@HooksWireup()
@Component({
    selector: 'ms-tournament',
    templateUrl: 'tournament.html',
    providers: [ObservableGridFactory],
})
export class TournamentComponent extends OnRouteResolve<TournamentViewModel> {
    error: boolean;
    sport: SportModel;
    viewType = TournamentView;

    live?: FixtureSection;
    preMatch?: FixtureSection;
    leagueList?: LeagueSection;

    constructor(
        public sitecore: Sitecore,
        private redirectHelper: RedirectHelperService,
        private gridFactory: ObservableGridFactory,
        route: ActivatedRoute,
    ) {
        super(route);
    }

    goBack(): void {
        this.redirectHelper.goBack();
    }

    getView(participants: TournamentParticipant[]): TournamentView {
        if (!participants?.length) {
            return TournamentView.Club;
        }

        const areCountries = participants.every((participant) => participant.id > 0);
        const areMany = participants.length > 1;

        if (areMany) {
            return areCountries ? TournamentView.MultiCountry : TournamentView.MultiClub;
        } else {
            return areCountries ? TournamentView.Country : TournamentView.Club;
        }
    }

    getParticipants(participants: TournamentParticipant[]): string {
        if (!participants) {
            return '';
        }

        return participants.map((x) => x.name).join(', ');
    }

    onRouteResolved(model: TournamentViewModel): void {
        this.error = false;

        if (!model || !this.isValid(model)) {
            this.error = true;

            return;
        }

        this.sport = model.sport;
        this.live = this.getFixtureSection(model, 'live');
        this.preMatch = this.getFixtureSection(model, 'preMatch');

        if (model.leagues.some((league) => league.count > 0)) {
            this.leagueList = {
                visible: model.sections.leagues.visible,
                title: model.sections.leagues.title,
                leagues: model.leagues,
            };
        }
    }

    private isValid(tournament: TournamentViewModel): boolean {
        if (!tournament.sections) {
            return false;
        }

        return this.isValidSection(tournament.sections.live) || this.isValidSection(tournament.sections.preMatch);
    }

    private isValidSection(section?: TournamentEventSection): section is TournamentEventSection {
        return Boolean(section?.events?.length);
    }

    private getFixtureSection(model: TournamentViewModel, key: 'live' | 'preMatch'): FixtureSection | undefined {
        const section = model.sections[key];

        if (!this.isValidSection(section)) {
            return;
        }

        const grid = this.gridFactory.fromResponse(`${this.sport}/tournaments/${kebabCase(key)}`, section.events, {
            grouping: GridGrouping.None,
            collapsedThreshold: 20,
        });

        return { ...section, grid };
    }
}
